/**
 * The message catalogue.
 *
 * English is the source: it is the only catalogue typed by hand, and every
 * other language is checked against its shape at compile time. A missing
 * Romanian key is a type error, not a screen that silently shows an English
 * word in the middle of a Romanian sentence.
 *
 * Keys are namespaced by where they appear, not by what they say. `nav.players`
 * survives a copy change; `players` as a key does not survive the day someone
 * needs a second word for the same thing.
 *
 * Interpolation is `{name}`. There is no logic in a message — no nesting, no
 * conditionals — because a translator should never have to read code. Plurals
 * are separate keys (`one` / `other`) chosen by `plural()`, which is what lets
 * Romanian's three-way rule work without the English catalogue knowing about it.
 */

export const en = {
  common: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    discard: "Discard",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    hidden: "Hidden",
    close: "Close",
    search: "Search",
    clear: "Clear",
    loading: "Loading…",
    signIn: "Sign in",
    signOut: "Sign out",
    tryAgain: "Try again",
    viewAll: "View all",
    unsaved: "You have unsaved changes.",
    somethingWentWrong: "Something went wrong",
    notFoundTitle: "That page does not exist",
    notFoundBody: "The link may be out of date, or the page may have moved.",
    noResultsTitle: "Nothing matches those filters",
    noResultsBody: "Try a broader search, or clear the filters to see everything again.",
    clearFilters: "Clear filters",
    previousPage: "Previous page",
    nextPage: "Next page",
    of: "of",
  },

  nav: {
    groupClub: "Club",
    groupMatchday: "Matchday",
    groupWebsite: "Website",
    groupOrganisation: "Organisation",
    dashboard: "Dashboard",
    dashboardHint: "Today's picture of the club",
    teams: "Teams",
    teamsHint: "Squads and age groups",
    matches: "Matches",
    matchesHint: "Competitions, fixtures and results",
    shop: "Shop",
    shopHint: "Products and orders",
    players: "Players",
    playersHint: "Every registered player",
    news: "News",
    newsHint: "Articles in every language you publish",
    site: "Site & design",
    siteHint: "Template, colours and domain",
    analytics: "Analytics",
    analyticsHint: "Traffic, sources and conversions",
    marketing: "Email",
    marketingHint: "Newsletters and offers",
    payments: "Card payments",
    paymentsHint: "The bank, and what it was told",
    tickets: "Ticketing",
    ticketsHint: "Stadium, matches and the gate",
    stadium: "Stadium",
    stadiumHint: "Stands, sectors, seats and gates",
    scanner: "Access control",
    scannerHint: "Validate tickets at a turnstile",
    settings: "Settings",
    settingsHint: "Tenant, languages and plan",
    staff: "Staff",
    staffHint: "Coaches, and who can sign in",
    collapse: "Collapse",
    expandSidebar: "Expand sidebar",
    collapseSidebar: "Collapse sidebar",
    openClubWebsite: "Open the club website",
    account: "Account",
    switchClub: "Switch club",
    current: "current",
    theme: "Colour theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    commandPlaceholder: "Search the club…",
    commandEmpty: "Nothing matches “{query}”.",
    goTo: "Go to",
  },

  workspace: {
    chooseTitle: "Choose a club",
    chooseSubtitle: "Signed in as {email}.",
    noAccessTitle: "No club access",
    noAccessBody:
      "This account is not linked to a club yet. Ask an administrator to invite you.",
    unknownSlug: "You do not have access to “{slug}”. Pick one of yours instead.",
    lastUsed: "last used",
  },

  dashboard: {
    viewSite: "View site",
    customise: "Customise",
    statPlayers: "Players",
    statPlayersHint: "Registered across every team",
    statTeams: "Teams",
    statTeamsHint: "Squads and age groups",
    statDrafts: "Drafts",
    statDraftsHint: "Articles not yet published",
    statAssistant: "Assistant",
    latestNews: "Latest news",
    latestNewsHint: "What is on the club website right now.",
    squads: "Squads",
    squadsHint: "Jump straight into a team's players.",
    getThingsDone: "Get things done",
    getThingsDoneHint: "The four things clubs do most.",
    writeArticle: "Write an article",
    writeArticleHint: "Match report, signing or announcement",
    registerPlayer: "Register a player",
    registerPlayerHint: "Add someone to a squad",
    changeDesign: "Change the design",
    changeDesignHint: "Template, colours and crest",
    languagesAndPlan: "Languages & plan",
    languagesAndPlanHint: "What the club publishes in",
    nothingPublished: "Nothing published yet.",
    writeFirst: "Write the first article",
    noTeams: "No teams yet.",
    playersAcrossTeams: "{players} registered players across {teams} teams",
    today: "today",
    yesterday: "yesterday",
    daysAgo: "{count} days ago",
  },

  players: {
    selectAll: "Select every player on this page",
    selectedCount: "{count} selected",
    moveTo: "Move to…",
    moved: "{count} moved",
    remove: "Remove",
    removeSure: "Remove them from the club?",
    removeConfirm: "Yes, remove",
    removed: "{count} removed",
    eyebrow: "Squad",
    title: "Players",
    description: "Everyone registered with the club, across every team.",
    register: "Register player",
    searchPlaceholder: "Search by name…",
    searchLabel: "Search players by name",
    allTeams: "All teams",
    allStatuses: "All statuses",
    filterTeam: "Filter by team",
    filterStatus: "Filter by status",
    emptyTitle: "No players yet",
    emptyBody:
      "Register your first player to start tracking squads, training and attendance.",
    columnPlayer: "Player",
    columnTeam: "Team",
    columnPosition: "Position",
    columnStatus: "Status",
    unassigned: "Unassigned",
    medicalTitle: "Medical records are separate",
    medicalBody:
      "Coaching staff see availability only. Diagnoses and treatment are held under a separate permission and never appear on a player profile.",
    registration: "Registration",
    registrationHint: "Status with the club and the federation.",
    profile: "Profile",
    profileHint: "Who the player is, on the pitch.",
    shirtNumber: "Shirt number",
    joinedClub: "Joined club",
    federationId: "Federation ID",
    dateOfBirth: "Date of birth",
    nationality: "Nationality",
    preferredFoot: "Preferred foot",
    positions: "Positions",
    notFound: "This player does not exist, or is not in a team you have access to.",
  },

  teams: {
    eyebrow: "Squads",
    title: "Teams",
    description: "Every squad in the club. Open one to see its players.",
    emptyTitle: "No teams",
    emptyBody: "You do not have access to any teams in this club.",
    senior: "Senior",
    seniorHint: "First team and reserves.",
    academy: "Academy",
    academyHint: "Age groups, youngest first.",
    playerCount_one: "player",
    playerCount_few: "players",
    playerCount_other: "players",
  },

  staff: {
    eyebrow: "People",
    title: "Staff",
    description: "Who runs the teams, and who can sign in to run the club.",
    technical: "Coaching staff",
    technicalHint: "Who runs a team. The head coach is shown on the club website.",
    technicalEmpty: "Nobody yet. Add the head coach first — he appears on the team's page.",
    team: "Team",
    firstName: "First name",
    lastName: "Last name",
    role: "Role",
    add: "Add",
    added: "Added.",
    onWebsite: "On the website",
    accounts: "Accounts",
    accountsHint: "Who can sign in here, and what they are allowed to touch.",
    accountsEmpty: "Only you, so far.",
    pending: "Invited",
    inviteTitle: "Invite somebody",
    inviteHint: "They get a login scoped to exactly what you choose here.",
    email: "Email",
    accessRole: "What they may do",
    roleTENANT_OWNER: "Owner",
    roleCLUB_ADMIN: "Club administrator",
    roleACADEMY_DIRECTOR: "Academy director",
    roleHEAD_COACH: "Head coach",
    roleCOACH: "Coach",
    roleTEAM_MANAGER: "Team manager",
    roleCONTENT_MANAGER: "Press & communications",
    roleFINANCE_MANAGER: "Finance",
    roleMEDICAL_STAFF: "Physio / medical",
    pickTeamFirst: "choose a team",
    chooseTeam: "Choose a team",
    outOfReach: "beyond your own access",
    roleOutOfReach: "You cannot give access you do not have yourself.",
    startingPassword: "Starting password (optional)",
    startingPasswordHint:
      "Leave empty and they get an invitation link to set their own — the better option. Fill it in and they must change it the first time they sign in.",
    startingPasswordPlaceholder: "At least 10 characters",
    sendInvite: "Send invitation",
    createAccount: "Create the account",
    invitedByLink: "Invitation sent.",
    invitedWithPassword: "Account created. Give them the password — they must change it at first sign-in.",
    inviteBlocked:
      "Giving somebody access needs two-factor authentication on your own account first. Set up an authenticator app in your account settings, sign in again, and the invite form appears here.",
    inviteRoles: "Roles you can give",
    setUpTwoFactor: "Set up two-factor authentication",
    noAccess: "No access",
    noAccessBody: "Your role does not include managing the club's staff.",
  },

  analytics: {
    eyebrow: "Website",
    title: "Analytics",
    description: "Who visits the club's website, where they come from, and what they do.",
    range: "Period",
    rangetoday: "Today",
    "range7d": "7 days",
    "range30d": "30 days",
    "range90d": "90 days",
    live: "On the site now",
    sessions: "Visits",
    visitors: "Unique visitors",
    views: "Page views",
    signups: "Sign-ups",
    vsPrevious: "vs previous period",
    noComparison: "No earlier period to compare",
    traffic: "Traffic",
    funnel: "From visit to order",
    funnelHint: "Counted in visits, not clicks.",
    stepVisits: "Visits",
    stepShop: "Opened the shop",
    stepCheckout: "Started checkout",
    stepOrders: "Ordered",
    sources: "Where they came from",
    sourcesEmpty: "Nobody has arrived from another site yet.",
    pages: "Most read",
    devices: "Device",
    browsers: "Browser",
    countries: "Countries",
    cities: "Cities",
    campaigns: "Campaigns",
    campaignsEmpty: "No campaign links used yet.",
    noData: "Nothing yet.",
    emptyTitle: "No visits recorded yet",
    emptyBody:
      "Numbers appear as soon as somebody opens the club website. Nothing needs installing.",
    privacy:
      "Counted without cookies. Visitors are identified by a hash that changes every day, so nobody is followed between days and there is no consent banner to show.",
  },

  marketing: {
    eyebrow: "Supporters",
    title: "Email",
    description: "Write to the people who asked to hear from the club.",
    newCampaign: "Send something",
    newCampaignHint: "Pick a letter, choose who gets it, see how many that is.",
    campaignName: "Name",
    campaignNamePlaceholder: "Offer — kit, August",
    template: "Letter",
    chooseTemplate: "Choose a letter",
    audience: "Who gets it",
    audienceNEWSLETTER: "Newsletter",
    audienceSUPPORTERS: "Account holders",
    audienceEVERYONE: "Everyone",
    peopleConsented: "people have agreed to hear from the club",
    sendNow: "Send now",
    send: "Send",
    sentTo: "Sent to {count} people.",
    preview: "Preview",
    templates: "Letters",
    templatesHint: "Reusable. The August kit offer goes out every August.",
    newTemplate: "New letter",
    editTemplate: "Edit letter",
    templateName: "Name",
    templateSaved: "Saved.",
    subject: "Subject",
    subjectHint: "What an inbox shows first. Keep it under sixty characters.",
    preheader: "Preview line",
    preheaderHint: "The line after the subject. Left empty, the client picks a sentence for you.",
    body: "Body",
    ctaLabel: "Button",
    ctaHint: "Most club emails have exactly one thing to click.",
    ctaUrl: "Button link",
    noTemplates: "No letters yet",
    noTemplatesBody: "Write one and you can send it whenever you need to.",
    history: "Sent",
    historyHint: "What went out, and to how many.",
    noCampaigns: "Nothing sent yet.",
    untitled: "Untitled",
    failed: "failed",
    statusDRAFT: "Draft",
    statusSCHEDULED: "Scheduled",
    statusSENDING: "Sending",
    statusSENT: "Sent",
    statusFAILED: "Failed",
    statusCANCELLED: "Cancelled",
    consentNote:
      "Only people who gave consent are ever written to, and every email carries a one-click unsubscribe. Somebody who leaves is gone from the next send immediately.",
  },

  matches: {
    eyebrow: "Matchday",
    title: "Competitions & fixtures",
    description:
      "The competitions you are in, the fixtures you play, and the results. Everything here shows on the club website.",

    competitions: "Competitions",
    competitionsHint: "One entry per competition per season.",
    join: "Enter a competition",
    joinTitle: "Enter a competition",
    joinBody:
      "Pick the competition and name the season. If another club has already entered this season, you join theirs.",
    competition: "Competition",
    season: "Season",
    seasonPlaceholder: "2025/26",
    startDate: "Season starts",
    endDate: "Season ends",
    joined: "Entered {competition}.",
    noCompetitions: "Not in a competition yet",
    noCompetitionsBody:
      "Enter one and your fixtures, results and league table appear on the website.",

    fixtures: "Fixtures",
    fixturesHint: "Scheduled first, then played.",
    addFixture: "Add a fixture",
    addFixtureTitle: "Add a fixture",
    opponent: "Opponent",
    opponentSearch: "Search clubs…",
    opponentMissing: "Not in the list? Add “{name}”",
    opponentAdded: "Added {name} to the club directory.",
    venue: "Ground",
    homeAway: "Where",
    home: "Home",
    away: "Away",
    kickoff: "Kick-off",
    kickoffTbc: "Time to be confirmed",
    round: "Round",
    roundNumber: "Matchday number",
    ticketUrl: "Ticket link",
    ticketUrlHint: "Shown as a Buy tickets button next to this fixture.",
    noFixtures: "No fixtures yet",
    noFixturesBody: "Add one and it appears on the website under Next matches.",
    fixtureAdded: "Fixture added.",

    result: "Result",
    editFixture: "Fixture",
    edit: "Edit",
    recordResult: "Record result",
    recordResultBody: "Enter the final score. The league table updates from it.",
    scoreHome: "Home",
    scoreAway: "Away",
    resultSaved: "Result saved.",
    saved: "Saved.",
    status: "Status",
    statusSCHEDULED: "Scheduled",
    statusPOSTPONED: "Postponed",
    statusCANCELLED: "Cancelled",
    statusFINISHED: "Finished",
    statusAWARDED: "Awarded",

    table: "League table",
    tableHint: "Computed from results, never edited.",
    position: "#",
    club: "Club",
    played: "P",
    won: "W",
    drawn: "D",
    lost: "L",
    goalsFor: "GF",
    goalsAgainst: "GA",
    goalDifference: "GD",
    points: "Pts",
    form: "Form",
    tableEmpty: "The table fills in as results are recorded.",
    knockoutNoTable: "A knockout competition has no table.",
  },

  squads: {
    staffPhoto: "Photo",
    add: "Add a team",
    addTitle: "New team",
    addBody: "A name and a short code is enough — everything else can change later.",
    edit: "Edit",
    editTitle: "Edit team",
    name: "Name",
    code: "Code",
    codeHint: "Shown on badges and in tables. Two or three letters.",
    ageGroup: "Age group",
    ageGroupPlaceholder: "U15",
    level: "Level",
    gender: "Gender",
    isAcademy: "Academy team",
    isAcademyHint: "Academy squads are grouped separately from the senior side.",
    sport: "Sport",
    sportHint: "What this team plays. Everything else on the club stays as it is.",
    sportFOOTBALL: "Football",
    sportFUTSAL: "Futsal",
    sportHANDBALL: "Handball",
    sportBASKETBALL: "Basketball",
    sportVOLLEYBALL: "Volleyball",
    sportRUGBY: "Rugby union",
    sportICE_HOCKEY: "Ice hockey",
    sportWATER_POLO: "Water polo",
    staff: "Coaching staff",
    staffHint: "Who runs this team. The head coach is shown on the club website.",
    staffAdd: "Add",
    staffFirstName: "First name",
    staffLastName: "Last name",
    staffRole: "Role",
    staffTitle: "Job title",
    staffTitleHint: "Only if the club's own word differs.",
    staffPublic: "Show on the website",
    staffEmpty: "Nobody yet. Add the head coach first — they appear on the team's page.",
    staffRemove: "Remove",
    roleHEAD_COACH: "Head coach",
    roleASSISTANT_COACH: "Assistant coach",
    roleGOALKEEPING_COACH: "Goalkeeping coach",
    roleFITNESS_COACH: "Fitness coach",
    roleANALYST: "Analyst",
    rolePHYSIO: "Physio",
    roleDOCTOR: "Club doctor",
    roleTEAM_MANAGER: "Team manager",
    roleKIT_MANAGER: "Kit manager",
    rolePRESS_OFFICER: "Press officer",
    rolePRESIDENT: "President",
    roleDIRECTOR: "Director",
    archive: "Archive",
    archiveTitle: "Archive this team?",
    archiveBody:
      "It disappears from the list but keeps its registrations and results. You can bring it back.",
    restore: "Restore",
    created: "Team added.",
    updated: "Team updated.",
    archived: "Team archived.",

    levelFIRST: "First team",
    levelRESERVE: "Reserve",
    levelYOUTH: "Youth",
    levelFUTSAL: "Futsal",
    levelOTHER: "Other",
    genderMALE: "Male",
    genderFEMALE: "Female",
    genderMIXED: "Mixed",
  },

  playerEdit: {
    edit: "Edit player",
    details: "Details",
    detailsHint: "Name, date of birth and position.",
    photo: "Photo",
    photoHint: "Portrait, shown on the squad page.",
    firstName: "First name",
    lastName: "Last name",
    birthDate: "Date of birth",
    position: "Position",
    foot: "Preferred foot",
    federationId: "Federation number",
    joinedOn: "Joined the club",
    saved: "Player updated.",

    squad: "Squad",
    squadHint: "Moving a player ends their old registration and opens a new one.",
    team: "Team",
    noTeam: "No squad",
    shirtNumber: "Shirt number",
    moved: "Squad updated.",
    status: "Status",
    statusTRIAL: "On trial",
    statusREGISTERED: "Registered",
    statusLOANED_OUT: "Out on loan",
    statusINACTIVE: "Inactive",
    statusDEPARTED: "Departed",

    footLEFT: "Left",
    footRIGHT: "Right",
    footBOTH: "Both",
  },

  shop: {
    eyebrow: "Shop",
    title: "Club shop",
    description:
      "What the club sells, and who is waiting to collect. Payment is taken at the counter.",

    products: "Products",
    productsHint: "Hidden products stay in the catalogue but leave the shop.",
    add: "Add a product",
    addTitle: "New product",
    editTitle: "Edit product",
    name: "Name",
    productDescription: "Description",
    descriptionHint: "A sentence or two. Shown on the shop page.",
    price: "Price",
    image: "Picture",
    imageHint: "Shown in the shop and in the basket.",
    visible: "In the shop",
    visibleHint: "Turn this off to take it off the shop page without deleting it.",
    sizes: "Sizes and stock",
    sizesHint: "One row per size. A product without sizes gets a single “One size”.",
    sizeLabel: "Size",
    sku: "Code",
    stock: "In stock",
    addSize: "Add a size",
    created: "Product added.",
    updated: "Product updated.",
    deleted: "Product removed.",
    remove: "Remove",
    removeConfirm: "Remove this product?",
    noProducts: "Nothing in the shop yet",
    noProductsBody: "Add a scarf, a shirt or a programme and it appears on the club site.",
    soldOut: "Sold out",
    lowStock: "{count} left",

    orders: "Orders",
    ordersHint: "Waiting to be collected, newest first.",
    reference: "Reference",
    buyer: "Buyer",
    placed: "Placed",
    total: "Total",
    collect: "Collected",
    cancel: "Cancel",
    collected: "Marked collected.",
    cancelled: "Order cancelled, stock returned.",
    noOrders: "No orders yet",
    noOrdersBody: "Orders placed on the club site appear here.",
    statusAWAITING_COLLECTION: "To collect",
    statusCOLLECTED: "Collected",
    statusCANCELLED: "Cancelled",
    statusPENDING: "Pending",
  },

  news: {
    cover: "Cover image",
    coverHint: "Shown in the news carousel on the front page and on the article.",
    eyebrow: "Newsroom",
    title: "News",
    description: "Articles for the club website, in every language you publish.",
    newArticle: "New article",
    emptyTitle: "No articles yet",
    emptyBody: "Match reports, signings and announcements all live here.",
    writeFirst: "Write the first one",
    columnTitle: "Title",
    columnType: "Type",
    columnStatus: "Status",
    columnLanguages: "Languages",
    columnPublished: "Published",
    allTypes: "All types",
    filterType: "Filter by article type",
    languageReady: "{locale} is ready to publish",
    languageIncomplete: "{locale} still needs a body",
    untitled: "Untitled",
    whatKind: "What kind of article?",
    articleTitle: "Title",
    titlePlaceholder: "What happened, in one line",
    summary: "Summary",
    summaryHint: "Shown in the news list and in link previews.",
    summaryPlaceholder: "One or two sentences",
    publishing: "Publishing",
    publishNow: "Publish now",
    schedule: "Schedule",
    sendForReview: "Send for review",
    backToDraft: "Back to draft",
    archive: "Archive",
    publishAt: "Publish at",
    saveFirst:
      "Save your changes first — publishing sends what is stored, not what is on screen.",
    missingLocales:
      "Not yet written in {locales}. You can still publish — readers in those languages will see the one that is ready.",
    discardChanges: "Discard unsaved changes?",
    created: "Article created",
    createdHint: "It is a draft until you publish it.",
    savedIn: "Saved in {locale}",
  },

  blocks: {
    paragraph: "Paragraph",
    heading: "Subheading",
    quote: "Quote",
    list: "List",
    bulleted: "Bulleted",
    numbered: "Numbered",
    addItem: "Add item",
    moveUp: "Move up",
    moveDown: "Move down",
    removeBlock: "Remove block",
    sectionTitle: "Section title",
    quotePlaceholder: "What was actually said",
    attributionPlaceholder: "Who said it",
    paragraphPlaceholder: "Write the paragraph",
    nothingYet: "Nothing here yet.",
  },

  assistant: {
    title: "Writing assistant",
    tagline: "It tightens your words. It never adds facts.",
    improve: "Improve the text",
    improving: "Improving…",
    headlines: "Suggest headlines",
    thinking: "Thinking…",
    keepMine: "Keep mine",
    useSuggestion: "Use the suggestion",
    yours: "Yours",
    suggested: "Suggested",
    headlinesTitle: "Headlines",
    left: "left",
    emptyDraft:
      "Write a title and a first paragraph, then the assistant has something to work on.",
    unavailable: "The assistant is unavailable right now.",
    notInPlan: "The writing assistant is not included in your plan.",
  },

  ticketing: {
    eyebrow: "Matchday",
    stadiumTitle: "Stadium",
    stadiumDescription:
      "The ground as the club draws it. A published layout is frozen: matches take their own copy of it, so redrawing a stand never moves somebody who has already bought a seat.",
    matchesTitle: "Matches",
    matchesDescription:
      "Fixtures with tickets on sale. Each one holds its own frozen stadium and its own inventory.",
    scannerTitle: "Access control",
    scannerDescription:
      "Validate a ticket at a gate. The same endpoints the mobile scanner will use.",
    configuration: "Configuration",
    version: "Version",
    draft: "Draft",
    published: "Published",
    archived: "Archived",
    publish: "Publish",
    publishHint: "Freezes this layout. Editing afterwards creates a new version.",
    newVersion: "Edit as new version",
    capacity: "Capacity",
    reservedSeats: "Reserved seats",
    generalAdmission: "General admission",
    blockedSeats: "Blocked",
    accessibleSeats: "Accessible",
    byStand: "By stand",
    bySector: "By sector",
    warnings: "Warnings",
    noWarnings: "Nothing to flag.",
    cannotPublish: "This configuration cannot be published yet.",
    publishedToast: "Stadium configuration published.",
    forkedToast: "New draft version created.",
    sector: "Sector",
    sectors: "Sectors",
    seats: "seats",
    gate: "Gate",
    gates: "Gates",
    priceZones: "Price zones",
    noStadium: "No stadium yet",
    noStadiumHint: "Create a venue and draw its stands to start selling tickets.",
    noMatches: "No matches on sale",
    noMatchesHint: "Create a match from a published stadium configuration.",
    kickoff: "Kick-off",
    doorsOpen: "Doors open",
    sold: "Sold",
    available: "Available",
    held: "In baskets",
    allocated: "Allocated",
    occupancy: "Occupancy",
    revenue: "Revenue",
    grossRevenue: "Gross",
    netRevenue: "Net of VAT",
    ticketsIssued: "Tickets issued",
    seasonTickets: "Season tickets",
    admitted: "Admitted",
    noShows: "No-shows",
    refused: "Refused",
    byTicketType: "By ticket type",
    byGate: "By gate",
    selectEvent: "Choose a match",
    selectGate: "Choose a gate",
    startScanning: "Start scanning",
    stopScanning: "Stop",
    manualEntry: "Or type a ticket code",
    scan: "Validate",
    scanAgain: "Next ticket",
    recentScans: "Recent scans",
    noScans: "Nothing scanned yet.",
    cameraUnavailable: "No camera available on this device. Type the code instead.",
    resultValid: "Welcome in",
    resultAlreadyUsed: "Already scanned",
    resultWrongGate: "Wrong gate",
    resultWrongEvent: "Wrong match",
    resultNotYetValid: "Too early",
    resultExpired: "Expired",
    resultCancelled: "Cancelled",
    resultRefunded: "Refunded",
    resultDeviceRevoked: "This scanner is revoked",
    resultUnknown: "Not a valid ticket",
    firstSeen: "First admitted",
    addVenue: "Add a stadium",
    addVenueHint: "The ground itself: where it is, how big it is, which way the pitch lies.",
    venueName: "Stadium name",
    venueCode: "Internal code",
    venueCodeHint: "Short handle used on printed tickets and exports.",
    city: "City",
    address: "Address",
    expectedCapacity: "Expected capacity",
    pitchOrientation: "Pitch orientation",
    addConfiguration: "Start a configuration",
    addConfigurationHint:
      "A named, versioned layout — football at full capacity, a reduced one, a concert. Matches are created from a published configuration.",
    configurationName: "Configuration name",
    noPriceZones: "No price zones yet.",
    addPriceZone: "Add a price zone",
    zoneName: "Zone name",
    zoneColour: "Colour",
    priceZone: "Price zone",
    stands: "Stands",
    noStands: "No stands yet.",
    addStand: "Add a stand",
    addStandHint: "Pick the side of the ground; the shape follows from it.",
    standName: "Stand name",
    side: "Side",
    sideHint: "Where it sits relative to the pitch.",
    sideWest: "West (main stand)",
    sideEast: "East",
    sideNorth: "North (behind the goal)",
    sideSouth: "South (behind the goal)",
    addSector: "Add a sector",
    sectorName: "Sector name",
    reservedSeating: "Numbered seats",
    reservedSeatingHint:
      "On: rows and seats a supporter picks from a map. Off: general admission with a capacity and no seat identity.",
    noGates: "No gates yet.",
    addGate: "Add a gate",
    addGateHint: "A way in, and which sectors it admits to.",
    gateName: "Gate name",
    gateCode: "Code",
    gateKind: "Kind",
    supporterSide: "Admits",
    sideAny: "Any supporter",
    sideHome: "Home only",
    sideAway: "Away only",
    accessibleEntrance: "Accessible entrance",
    sectorsServed: "Sectors this gate serves",
    sectorsServedHint: "A ticket only opens the gates that serve its sector.",
    generateSeats: "Generate seats",
    regenerate: "Replace all seats",
    regenerateWarning:
      "This sector already has rows. Generating again deletes every seat in it and creates new ones.",
    rowCount: "Number of rows",
    seatsPerRow: "Seats per row",
    rowStart: "First row label",
    firstSeatNumber: "First seat number",
    rowLabels: "Row labels",
    numbering: "Numbering runs",
    leftToRight: "Left to right",
    rightToLeft: "Right to left",
    wheelchairSeats: "Wheelchair spaces",
    obstructedSeats: "Obstructed view",
    seatAddressHint: "Row and seat, comma separated — A:1, A:2",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    resetView: "Reset",
    filling: "Filling up",
    almostGone: "Almost gone",
    unavailable: "Unavailable",
  },
  matchday: {
    live: "Live",
    addEvent: "Record what happened",
    addEventHint:
      "The league feed carries goals within a few minutes and cards late or not at all below the top divisions. Anything you put in here is kept — the feed will not overwrite it.",
    minute: "Minute",
    player: "Player",
    playerHint: "Optional",
    forWhom: "For",
    eventGoal: "Goal",
    eventYellow: "Yellow card",
    eventRed: "Red card",
    eventSubstitution: "Substitution",
    recorded: "Recorded.",
    lineupTitle: "Starting eleven",
    lineupWaiting:
      "The team sheet arrives from the league feed about an hour before kick-off — and later than that for smaller divisions.",
    addPlayer: "Add a player",
    lineupSaved: "Lineup saved.",
    tapAPlayer: "Tap a player, then a position",
    tapASlot: "Now tap where they play",
    emptySlot: "Empty position",
    everyonePlaced: "Everyone is on the pitch.",
    arrangedByClub: "Arranged by the club",
    roundTitle: "Round",
    roundHint:
      "Providers mislabel cup rounds — a preliminary tie can arrive as \"Final\". Your correction is stored separately, so the feed will not undo it.",
    roundLabel: "Show this round as",
    roundSaved: "Round updated.",
  },
  payments: {
    eyebrow: "Money",
    title: "Card payments",
    description:
      "Taking a supporter's card in the club shop. The bank issues these credentials to the club once its processing contract is signed — until they are here and switched on, the shop takes payment at the counter only.",
    userName: "User name",
    password: "Password",
    passwordHint: "As the bank issued it.",
    passwordKeptHint: "Leave blank to keep the one already stored. It cannot be shown again.",
    passwordStored: "Password set",
    sandbox: "Test gateway",
    sandboxHint:
      "Cards are not charged and no money moves. Turn this off only with the credentials the bank issued for real payments.",
    acceptCards: "Accept cards",
    acceptCardsHint:
      "Until this is on, the shop does not offer to take a card at all. Leave it off while you are testing.",
    check: "Check the credentials",
    checkPassed: "The bank answered. These credentials work.",
    saved: "Saved",
    live: "Taking cards",
    notLive: "Not taking cards",
    callsTitle: "Everything said to the bank",
    callsHint:
      "Every call, kept as it was sent. This is what to show the bank when the two of you disagree about a payment.",
    noCalls: "Nothing yet.",
  },
  site: {
    title: "Site & design",
    description:
      "How {club}'s public website looks. Four layouts, one content model — switching template changes how the site looks, never what it contains.",
    template: "Template",
    templateHint: "Pick the layout. Each one is shown in your own colours.",
    colours: "Colours",
    coloursHint: "Contrast is checked with the same maths the live site renders with.",
    startFrom: "Start from",
    resolvedPalette: "Resolved palette — what the site will actually use",
    identity: "Identity",
    identityHint: "What appears under the club name.",
    footer: "Footer",
    footerHint: "Contact, address and the legal line at the bottom of every page.",
    footerEmail: "Email",
    footerPhone: "Phone",
    footerAddress: "Address",
    footerAddressHint: "Written the way you would write it on a letter. Line breaks are kept.",
    footerLegal: "Legal line",
    footerLegalHint: "Company registration, VAT number — whatever your club has to print.",
    footerSponsorsTitle: "Partners heading",
    footerSponsorsHint: "Only shown once you have added a partner.",
    footerSponsorsPlaceholder: "Partners",
    clubName: "Club name",
    clubNameHint: "As it appears in the masthead and the footer.",
    wordmark: "Short name",
    wordmarkHint: "Next to the crest in the header.",
    tagline: "Tagline",
    taglineHint: "One short line. It sits under the crest on the home page.",
    colourMode: "Colour mode",
    colourModeHint: "How much of the club's colour the public site uses.",
    livePreview: "Live site",
    reloadPreview: "Reload the preview",
    openInNewTab: "Open in a new tab",
    previewDevice: "Preview device",
    desktop: "Desktop",
    mobile: "Mobile",
    unsavedDesign: "You have unsaved design changes.",
    saveChanges: "Save changes",
    saved: "Design saved",
    savedHint: "The club website updates within a few seconds.",
    couldNotSave: "Could not save",
    focalPointHint: "Click the part that must stay in shot",
    primary: "Primary",
    primaryHint: "Buttons, active navigation, links and focus rings.",
    secondary: "Secondary",
    secondaryHint: "Supporting accents. Optional.",
    accent: "Accent",
    accentHint: "Occasional highlights. Optional.",
    contrastOnWhite: "{ratio}:1 on white",
    textUses: "text uses",
  },

  /** The club's own website. A separate audience from the admin — supporters,
      not volunteers running the club — so it gets its own words. */
  publicSite: {
    home: "Home",
    news: "News",
    teams: "Teams",
    club: "Club",
    nextMatches: "Next matches",
    nextMatch: "Next match",
    liveNow: "Live",
    events: "Match events",
    goal: "Goal",
    ownGoal: "Own goal",
    penalty: "Penalty",
    missedPenalty: "Penalty missed",
    yellowCard: "Yellow card",
    redCard: "Red card",
    substitution: "Substitution",
    calendar: "Calendar",
    matchday: "Matchday",
    kickoffTbc: "TBC",
    clubFeed: "From the club",
    newsletterTitle: "Never miss a matchday",
    newsletterBody: "Fixtures, results, signings and ticket news, straight to your inbox.",
    newsletterEmail: "Your email address",
    newsletterSubmit: "Subscribe",
    newsletterDone: "You are on the list. See you at the ground.",
    newsletterFailed: "That did not go through. Try again in a moment.",
    newsletterConsent: "You can unsubscribe from any email the club sends.",
    clubFeedHint: "News, signings, departures and everything else.",
    typeANNOUNCEMENT: "News",
    typeMATCH_REPORT: "Match report",
    typeMATCH_PREVIEW: "Preview",
    typeSIGNING: "Signing",
    typeDEPARTURE: "Departure",
    typeACADEMY: "Academy",
    typeINTERVIEW: "Interview",
    days: "Days",
    hours: "Hours",
    minutes: "Mins",
    secondsShort: "Secs",
    fullCalendar: "See the calendar",
    previous: "Previous",
    next: "Next",
    results: "Results",
    standings: "Table",
    buyTickets: "Buy tickets",
    played: "P",
    goalDifference: "GD",
    differenceGOAL: "GD",
    differencePOINT: "PD",
    differenceSET: "SD",
    unitGOAL: "Goals",
    unitPOINT: "Points",
    unitSET: "Sets",
    points: "Pts",
    form: "Form",
    latestNews: "Latest news",
    allNews: "All news",
    ourTeams: "Our teams",
    senior: "Senior",
    seniorFootball: "Senior football",
    academy: "Academy",
    theClub: "The club",
    history: "The club's record",
    historyLead: "Season by season, and where it left us.",
    seasonCol: "Season",
    competitionCol: "Competition",
    positionCol: "Pos",
    playedCol: "P",
    recordCol: "W–D–L",
    honours: "Honours",
    ground: "Ground",
    capacity: "Capacity",
    founded: "Founded",
    established: "Established",
    country: "Country",
    timeZone: "Time zone",
    squad: "Squad",
    playerCount_one: "{count} player",
    playerCount_other: "{count} players",
    // Romanian's 2–19 form. English never selects it and so repeats itself,
    // which is the price of the catalogues sharing one shape.
    playerCount_few: "{count} players",
    teamsLead: "Every side the club puts out, from the first team to the youngest academy group.",
    viewSquad: "View the squad",
    firstTeam: "First team",
    roleHEAD_COACH: "Head coach",
    roleASSISTANT_COACH: "Assistant coach",
    roleGOALKEEPING_COACH: "Goalkeeping coach",
    roleFITNESS_COACH: "Fitness coach",
    roleANALYST: "Analyst",
    rolePHYSIO: "Physio",
    roleDOCTOR: "Club doctor",
    roleTEAM_MANAGER: "Team manager",
    roleKIT_MANAGER: "Kit manager",
    staffTitle: "Coaching staff",
    teamCount_one: "{count} team",
    rolePRESS_OFFICER: "Press officer",
    rolePRESIDENT: "President",
    roleDIRECTOR: "Director",
    teamCount_other: "{count} teams",
    teamCount_few: "{count} teams",
    academyGroups: "Academy groups",
    registeredPlayers: "Registered players",
    squadUnpublished: "The squad for this team has not been published yet.",
    squadComingSoon: "Squad to be announced",
    andMorePlayers: "+{count}",
    noNews: "No news yet.",
    staffSignIn: "Staff sign-in",
    footerContact: "Contact",
    footerAddress: "Address",
    footerFollow: "Follow",
    footerSponsors: "Partners",
    signIn: "Sign in",
    createAccount: "Create an account",
    signOut: "Sign out",
    myAccount: "My account",
    accountNav: "Account",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    accountLead: "Your details with this club, and everything you have bought here.",
    accountAnonTitle: "Sign in to your supporter account",
    accountAnonBody:
      "One account for everything you buy from the club. You can also check out as a guest.",
    myDetails: "Your details",
    myDetailsHint: "Only this club sees these.",
    myOrders: "Your orders",
    myOrdersHint: "Everything you have bought from the club shop.",
    noOrders: "Nothing yet. What you buy from the club shop shows up here.",
    detailsSaved: "Saved.",
    detailsFailed: "That did not save. Try again in a moment.",
    marketingOptIn: "Email me club news and offers",
    marketingHint: "You can turn this off at any time, and every email has an unsubscribe link.",
    closeAccount: "Close this account",
    closeAccountHint:
      "Removes your details from this club. Orders you have already placed stay with the club as its own records, but stop being linked to you.",
    closeAccountConfirm: "Close your account with this club?",
    orderPlacedOn: "Placed {date}",
    orderCollectedOn: "Collected {date}",
    statusPENDING: "Pending",
    statusAWAITING_COLLECTION: "Ready to collect",
    statusCOLLECTED: "Collected",
    statusCANCELLED: "Cancelled",
    shop: "Shop",
    shopOfficial: "Official store",
    shopLead: "Official club merchandise. Pay at the club shop when you collect.",
    shopEmpty: "The shop is not open yet.",
    addToBasket: "Add to basket",
    soldOut: "Sold out",
    lowStock: "{count} left",
    basket: "Basket",
    basketEmpty: "Your basket is empty.",
    total: "Total",
    checkout: "Checkout",
    yourName: "Your name",
    email: "Email",
    phone: "Phone",
    orderNote: "Anything we should know?",
    placeOrder: "Place the order",
    payOnCollection: "Pay at the club shop when you collect.",
    payByCard: "Pay by card",
    payAtCounter: "Pay when I collect",
    cardSafe: "You will be taken to your bank to pay.",
    orderPlaced: "Order placed",
    orderReference: "Give this number at the club shop when you collect.",
    orderDone: "Done",
    remove: "Remove",
    size: "Size",
    keepShopping: "Keep shopping",
    ticketsEyebrow: "Tickets",
    ticketsTitle: "Buy tickets",
    ticketsNone: "No matches on sale right now.",
    ticketsSeatsLeft: "left",
    ticketsSoldOut: "Sold out",
    ticketsChooseSector: "Choose where you want to sit",
    ticketsChooseSeats: "Choose your seats",
    ticketsBack: "All sectors",
    ticketsAvailable: "Available",
    ticketsUnavailable: "Taken",
    ticketsSelected: "Yours",
    ticketsFrom: "from",
    ticketsYourSeats: "Your seats",
    ticketsNoneChosen: "Pick a sector, then tap the seats you want.",
    ticketsBestAvailable: "Pick for me",
    ticketsHowMany: "How many",
    ticketsHoldExpires: "Held for you:",
    ticketsHoldExpired: "Your reservation ran out. Please choose again.",
    ticketsBuy: "Buy tickets",
    ticketsShow: "See my tickets",
    ticketsGeneralAdmission: "Standing — no numbered seats in this sector.",
    ticketsStanding: "standing",
    ticketsRowSeat: "Row/seat",
    ticketsGate: "Enter through",
    ticketsKeepSafe: "Show this code at the turnstile. A screenshot works.",
    lineupTitle: "Team sheets",
    lineupSubstitutes: "Substitutes",
    lineupCoach: "Coach",
    language: "Language",
    readMore: "Read more",
    translationMissing:
      "This article has not been translated yet — you are reading another language.",
  },

  settings: {
    eyebrow: "Organisation",
    title: "Settings",
    description: "What this tenant is, and what its plan includes.",
    demoData: "demo data",
    tenant: "Tenant",
    tenantHint: "Set when the club was onboarded.",
    legalName: "Legal name",
    tradingName: "Trading name",
    slug: "Slug",
    status: "Status",
    currency: "Default currency",
    timezone: "Timezone",
    languages: "Languages",
    languagesHint: "Every article can exist in each of these. The first is the default.",
    default: "default",
    interfaceLanguage: "Interface language",
    interfaceLanguageHint:
      "What language this admin is shown in. Set for the whole club, and you can override it for yourself.",
    assistant: "Writing assistant",
    assistantHint: "Included in your plan and paid for by the platform.",
    available: "Available",
    notAvailable: "Not available",
    assistantBody:
      "Improves the wording of an article you have already written. It never adds facts.",
    thisMonth: "This month",
    yourAccess: "Your access",
    yourAccessHint: "What {email} may do in {club}.",
  },
} as const;

/**
 * Every catalogue has exactly the shape of the English one — the same sections
 * and the same keys — while being free to hold different strings.
 *
 * The widening matters: `as const` on the English catalogue types each value as
 * its own literal, so without this a Romanian translation would be rejected for
 * not being the English sentence. What we want to enforce is the *shape*, so a
 * missing or misspelt key is a compile error and a different wording is not.
 */
type Widen<T> = { [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };

export type Catalogue = Widen<typeof en>;
